import { browser } from '$app/environment';
import { PUBLIC_GMAPS_API_KEY, PUBLIC_GMAPS_DARK_MAP_ID, PUBLIC_GMAPS_LIGHT_MAP_ID, PUBLIC_IMG_HOST } from '$env/static/public';
import { Vertical } from '$lib/def/verticals';
import { Theme } from '$lib/serv/theme';
import * as gmaps from '@googlemaps/js-api-loader';
import { from, Observable, of, Subscription, switchMap } from 'rxjs';


/**
 * This is a CLIENT-SIDE ONLY service.
 */

let loader: gmaps.Loader;
let loaded = false;
let loadPromise: Promise<any[]>;
export async function load() {
	if (!browser) return Promise.reject('Google Maps SDK is only available in the browser.');
	if (loaded) return Promise.resolve();
	if (loadPromise) return loadPromise;

	loader = new gmaps.Loader({
		apiKey: PUBLIC_GMAPS_API_KEY,
		version: 'quarterly',
		libraries: ['places','geometry','marker'],
	});
	loadPromise = Promise.all([
		loader.importLibrary('places'),
		loader.importLibrary('geometry'),
		loader.importLibrary('marker'),
	])
	await loadPromise;
	loaded = true;
}

let autocompleteService: google.maps.places.AutocompleteService;
let placesService: google.maps.places.PlacesService;
let geocoder: google.maps.Geocoder;
// let directionsService: google.maps.DirectionsService;

const MARKER_HEIGHT = 50;

function init() {
	if (!loaded) throw new Error('Google Maps SDK has not been loaded yet.');
	autocompleteService = new google.maps.places.AutocompleteService();
	placesService = new google.maps.places.PlacesService(document.createElement('div'));
	geocoder = new google.maps.Geocoder();
	// directionsService = new google.maps.DirectionsService();
}

/**
 * From https://developers.google.com/maps/documentation/places/web-service/session-tokens:
 * 
 * Place Autocomplete uses session tokens to group the query and selection phases of a
 * user autocomplete search into a discrete session for billing purposes. The session
 * begins when the user starts typing a query, and concludes when they select a place
 * and a call to Place Details is made. Each session can have multiple autocomplete
 * queries, followed by one place selection.
 * 
 * The lifetime of a session token consist of the following:
 * ```
 * var token = getSessionToken();
 * streamPlacePredictions(..., token).subscribe(predictions => {...});
 * ...
 * var place = await promisePlaceDetails(prediction.placeId, token); // final use for the token
 * ```
 */
export const getSessionToken = async () => {
	await load();
	return new google.maps.places.AutocompleteSessionToken();
}

export function predictor(sessionToken:google.maps.places.AutocompleteSessionToken) {
	return ($:Observable<string>) => {
		return $.pipe(
			switchMap(input => {
				if (!input) return of(null);
				if (!autocompleteService) init();
				// if (!autocompleteService) return throwError(() => new Error('Google Maps API is not loaded.'));
				return from(autocompleteService.getPlacePredictions({
					input, //user text string input
					types: ['address'],
					componentRestrictions: { country: 'za' },
					sessionToken,
				}));
			}),
		);
	}
}

export async function promisePlaceDetails(placeId:string, sessionToken:google.maps.places.AutocompleteSessionToken) {
	await load();
	if (!autocompleteService) init();
	return new Promise<google.maps.places.PlaceResult>((resolve,reject) => {
		placesService.getDetails({
			fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
			placeId,
			sessionToken,
		}, (result, status) => {
			if (!result || status !== google.maps.places.PlacesServiceStatus.OK) {
				reject('Could not resolve Google placeId');
			} else {
				resolve(result);
			}
		})
	})
}

export async function promiseGeoAddress(lat:number, lng:number) {
	await load();
	if (!autocompleteService) init();
	const { results } = await geocode({ location: { lat, lng } });
	return results;
}

export async function promisePlaceAddress(placeId:string) {
	await load();
	if (!autocompleteService) init();
	const { results } = await geocode({ placeId });
	return results;
}

async function geocode(
	request: google.maps.GeocoderRequest,
	attempts = 3,
):Promise<{ results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus }> {
	let status: google.maps.GeocoderStatus = google.maps.GeocoderStatus.ERROR;
	let results: google.maps.GeocoderResult[] = [];
	while (attempts-- && (status === google.maps.GeocoderStatus.ERROR || status === google.maps.GeocoderStatus.UNKNOWN_ERROR)) {
		({ results, status } = await geocodePromise(request));
	}
	
	return {results: results??[],status};
}

async function geocodePromise(request: google.maps.GeocoderRequest):Promise<{results:google.maps.GeocoderResult[],status:google.maps.GeocoderStatus}> {
	return new Promise(resolve => {
		geocoder.geocode(request, (results, status) => {
			resolve({results: results??[], status});
		})
	})
}

export function setupDriverTracking(
	node: HTMLElement,
	options:{
		merchantPosition?: google.maps.LatLngLiteral,
		customerPosition?: google.maps.LatLngLiteral, // optional (collect orders have no customer position)
		driver$?: Observable<google.maps.LatLngLiteral | undefined>,
		estTimeBetweenDriverUpdates?: number, // optional (default 9000ms)
		theme$?: Observable<Theme>,
		vertical: Vertical,
	}
) {
	const mapPadding = 0.5 * MARKER_HEIGHT;  // if marker's position is on an edge of the map, the padding will ensure at least 1/4 of the marker is visible
	const { merchantPosition, customerPosition, driver$, theme$, vertical } = options;
	let subStyles: Subscription | undefined;
	let subTheme: Subscription | undefined;
	let subDriver: Subscription | undefined;

	let animStart = 0;
	const animTotal = options.estTimeBetweenDriverUpdates ?? 9000;// est. time between firebase geolocation updates
	let animElapsed = 0;
	let animInProgress = false;
	let driverMarker: google.maps.marker.AdvancedMarkerElement;
	let lastDriverPosition: google.maps.LatLngLiteral | undefined;
	let targetDriverPosition: google.maps.LatLngLiteral | undefined;

	const animDriverFrame = (timestamp:number) => {
		if (!animInProgress) return;
		animElapsed = timestamp - animStart;
		const progress = animElapsed / animTotal;
		let pos: ReturnType<typeof google.maps.geometry.spherical.interpolate>;
		if (lastDriverPosition && targetDriverPosition) {
			pos = google.maps.geometry.spherical.interpolate(lastDriverPosition, targetDriverPosition, progress);
			driverMarker.position = pos; // TODO: expand functionality to making fitBounds part of the animation
		}
		if (progress > 1) {
			animInProgress = false;
		} else {
			requestAnimationFrame(animDriverFrame);
		}
	}

	const animDriver = () => {
		animStart = performance.now();
		if (animInProgress) return;
		animInProgress = true;
		requestAnimationFrame(animDriverFrame);
	}

	load().then(() => {
		subTheme = (theme$ ?? of(Theme.Light))
			.subscribe({
				next(theme) {
					const map = new google.maps.Map(node, {
						disableDefaultUI: true,
						disableDoubleClickZoom: true,
						minZoom: 13,
						maxZoom: 19,
						// zoomControl: false,
						center: merchantPosition,
						mapTypeId: google.maps.MapTypeId.ROADMAP,
						mapTypeControl: false,
						mapId: theme === Theme.Light ? PUBLIC_GMAPS_LIGHT_MAP_ID : PUBLIC_GMAPS_DARK_MAP_ID,
					});

					const merchantIcon = document.createElement('img');
					merchantIcon.src = `${PUBLIC_IMG_HOST}/0x${MARKER_HEIGHT}/webapp/driver-tracking-${vertical === Vertical.Grocery ? "pnp" : "food"}-${theme}.png`;
					new google.maps.marker.AdvancedMarkerElement({
						map,
						zIndex: 0,
						title: `The ${vertical === Vertical.Grocery ? "store" : vertical}`,
						position: merchantPosition,
						content: merchantIcon,
					});
			
					const customerIcon = document.createElement('img');
					customerIcon.src = `${PUBLIC_IMG_HOST}/0x${MARKER_HEIGHT}/webapp/driver-tracking-house-${theme}.png`;
					customerPosition && new google.maps.marker.AdvancedMarkerElement({
						map,
						zIndex: 0,
						title: 'The customer',
						position: customerPosition,
						content: customerIcon,
					});
					lastDriverPosition = targetDriverPosition = merchantPosition;
			
					if (driver$) {
						const driverIcon = document.createElement('img');
						driverIcon.src = `${PUBLIC_IMG_HOST}/0x${MARKER_HEIGHT}/webapp/driver-tracking-driver-${theme}.png`;
						driverMarker = new google.maps.marker.AdvancedMarkerElement({
							map,
							zIndex: 1,
							title: 'Driver',
							content: driverIcon,
						});

						subDriver?.unsubscribe();  // Prevent multiple subscriptions if theme changes
						subDriver = driver$.subscribe(pos => {
							if (!pos) return;
							lastDriverPosition = targetDriverPosition;
							targetDriverPosition = pos;
							animDriver();
							const bounds = new google.maps.LatLngBounds();
							if (lastDriverPosition) bounds.extend(lastDriverPosition);
							if (customerPosition) bounds.extend(customerPosition);
							map.fitBounds(bounds, mapPadding);
							// TODO: gray out the map when driver position stopped for a while
						});
					}
			
					if (customerPosition) {
						// map.setZoom(14); // surely the bounds will be zoomed accordingly
						const bounds = new google.maps.LatLngBounds();
						if (merchantPosition) bounds.extend(merchantPosition);
						bounds.extend(customerPosition);
						map.fitBounds(bounds, mapPadding);
					} else {
						map.setZoom(16);
						if (merchantPosition) map.setCenter(merchantPosition);
					}
				},
			});
	})

	return {
		destroy() {
			subStyles?.unsubscribe();
			subTheme?.unsubscribe();
			subDriver?.unsubscribe();
		}
	};
}